import { reportWeekday } from '@/features/reports/lib/report-zone'

/**
 * The end-of-day estimate for the running-day dashboard
 * (CHG-2026-09-manager-reporting, D13).
 *
 * It is computed here rather than on the server, from two hourly series the reporting endpoint
 * already knows how to return: today's, and a window of finished days behind it. That costs no new
 * endpoint, and — more importantly — it keeps the number visibly a heuristic. A server-side
 * forecast would arrive in the JSON looking like a measured figure, and the first consumer to trust
 * it would be wrong.
 *
 * The estimate is what is in the till, plus the part of a typical comparable day that is still
 * ahead of us:
 *
 *     end of day  =  taken so far  +  (1 − share of a day normally done by now) × typical day
 *
 * That is the same figure as scaling today's takings by the share done so far and blending the
 * result with the typical day at that weight — the algebra cancels — but in this form it is one
 * readable sentence, and it cannot fall below the money already taken, so it needs no floor.
 *
 * Three properties, each of which the first version got wrong and each of which was measured
 * against production history before it was chosen:
 *
 * 1. The share comes from EIGHT comparable days, not one. At kostbar READY the share done before
 *    12:00 ranges from 26 % to 56 % across a fortnight of weekdays. One day out of that spread is
 *    not a sample, and on 15.09. it put the estimate 30 % below the day.
 * 2. The remainder is the TYPICAL day's, not today's scaled up. A morning's takings multiplied by
 *    a large factor is mostly noise, and the tile is at its wildest exactly when somebody reads it.
 * 3. The share counts the reference days' running hour at the fraction of it that has elapsed.
 *    Dropping that hour whole froze the number at the top of every hour, through the steepest part
 *    of the lunch trade.
 */
export interface HourlyPoint {
  /** `YYYY-MM-DDTHH`. */
  key: string
  revenueCents: number
}

export interface Forecast {
  /** Null when there is nothing to extrapolate from. The tile then says so. */
  endOfDayCents: number | null
  /** What a comparable day usually finishes at, for standing beside the estimate. */
  typicalDayCents: number | null
  soFarCents: number
  /** How many days the estimate rests on, so the tile can stay quiet on a thin sample. */
  referenceDays: number
}

export interface ForecastInput {
  /** The running day, `YYYY-MM-DD`. */
  today: string
  /** Today's hours, in any order. */
  hours: Array<HourlyPoint>
  /** Hours of the finished days behind today — several days, keyed by day and hour. */
  referenceHours: Array<HourlyPoint>
  /** The current Vienna hour, 0–23. */
  hour: number
  /** How far that hour has run, 0–1. */
  hourFraction: number
}

/** Enough to absorb an odd day, few enough to still describe the season the store is in. */
const REFERENCE_DAYS = 8

export function forecastEndOfDay({
  today,
  hours,
  referenceHours,
  hour,
  hourFraction,
}: ForecastInput): Forecast {
  const soFarCents = sum(hours.map((h) => h.revenueCents))

  const references = comparableDays(today, referenceHours)
  if (references.length === 0) {
    return {
      endOfDayCents: null,
      typicalDayCents: null,
      soFarCents,
      referenceDays: 0,
    }
  }

  const typicalDayCents = Math.round(median(references.map(dayTotal)))
  const shareDoneByNow = median(
    references.map((day) => elapsed(day, hour, hourFraction) / dayTotal(day)),
  )

  const base = {
    typicalDayCents,
    soFarCents,
    referenceDays: references.length,
  }

  // Before the first comparable hour has passed, a typical day has done none of itself by now and
  // the estimate would be the typical day entire, whatever today has done. Saying "not yet" is
  // right; printing a number that ignores the day in front of us is not.
  if (shareDoneByNow <= 0) return { ...base, endOfDayCents: null }

  // `soFarCents` is today's takings up to this minute — the window the rows come from ends at
  // "now", so the running hour is already in them at its true partial value. Only the reference
  // days, which are whole, need their running hour cut to the elapsed fraction.
  const stillToCome = (1 - shareDoneByNow) * typicalDayCents

  return { ...base, endOfDayCents: soFarCents + Math.round(stillToCome) }
}

/** Hourly cents by hour of day, 0–23. */
type DayCurve = Array<number>

/**
 * The most recent finished days of the same kind as today, newest first.
 *
 * Same kind rather than same weekday: a store two weeks old has at most two of any one weekday,
 * and a Tuesday and a Wednesday trade far more alike than a Tuesday and a Saturday do. Days with
 * no revenue are dropped — a closed day describes nothing.
 */
function comparableDays(
  today: string,
  referenceHours: Array<HourlyPoint>,
): Array<DayCurve> {
  const byDay = new Map<string, Array<HourlyPoint>>()
  for (const point of referenceHours) {
    const day = point.key.slice(0, 10)
    if (day >= today) continue
    const bucket = byDay.get(day)
    if (bucket) bucket.push(point)
    else byDay.set(day, [point])
  }

  return [...byDay.entries()]
    .filter(([day]) => isWeekend(day) === isWeekend(today))
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([, points]) => byHour(points))
    .filter((curve) => dayTotal(curve) > 0)
    .slice(0, REFERENCE_DAYS)
}

function isWeekend(day: string): boolean {
  return reportWeekday(day) >= 6
}

function byHour(points: Array<HourlyPoint>): DayCurve {
  const curve: DayCurve = Array.from({ length: 24 }, () => 0)
  for (const point of points) {
    const hour = Number(point.key.slice(11, 13))
    if (Number.isFinite(hour) && hour >= 0 && hour < 24) {
      curve[hour] += point.revenueCents
    }
  }
  return curve
}

function dayTotal(curve: DayCurve): number {
  return sum(curve)
}

/** A reference day's takings up to this point in it: the hours behind us, plus part of this one. */
function elapsed(curve: DayCurve, hour: number, hourFraction: number): number {
  const complete = sum(curve.slice(0, Math.max(0, Math.min(24, hour))))
  const running = hour >= 0 && hour < 24 ? curve[hour] * clamp(hourFraction) : 0
  return complete + running
}

function clamp(fraction: number): number {
  if (!Number.isFinite(fraction)) return 0
  return Math.min(1, Math.max(0, fraction))
}

function sum(values: Array<number>): number {
  return values.reduce((total, value) => total + value, 0)
}

function median(values: Array<number>): number {
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle]
}
